import * as XLSX from 'xlsx';
import { ChecklistNarrative, MonthEndChecklist } from './massBalanceStore';

/**
 * Export a Month End Checklist to a workbook the accountant can file with the close.
 *
 * Built in the browser from the data already on screen (xlsx is an existing dependency), so there is
 * no backend round-trip and the file always matches exactly what the user is looking at.
 *
 * Layout: a Summary sheet that is the whole close on one page, then one sheet per check holding its
 * FULL findings — not the truncated view — and Cal's write-up alongside the numbers it describes.
 */

// Finding keys carry the raw field name. "rho" is the stored name for implied density and means
// nothing to a reader of the workbook, so it is spelled out here as it is on screen.
const headerLabel = (k: string) =>
  k.replace(/_/g, ' ').replace(/\bpct\b/g, '%').replace(/\brho\b/g, 'density')
   .replace(/^./, (c) => c.toUpperCase());

const STATUS_LABEL: Record<string, string> = {
  fail: 'NEEDS FIXING', warn: 'CONFIRM', ok: 'PASSED', no_data: 'NO DATA',
};

// Excel sheet names: 31 chars max, and none of : \ / ? * [ ]
const sheetName = (label: string, used: Set<string>) => {
  let base = label.replace(/[:\\/?*[\]]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 28) || 'Check';
  let name = base, n = 2;
  while (used.has(name.toLowerCase())) name = `${base.slice(0, 26)} ${n++}`;
  used.add(name.toLowerCase());
  return name;
};

const autoWidth = (rows: any[][]) => {
  const w: number[] = [];
  rows.forEach((r) => r.forEach((c, i) => {
    w[i] = Math.min(60, Math.max(w[i] || 10, String(c ?? '').length + 2));
  }));
  return w.map((wch) => ({ wch }));
};

// Markdown bullets read badly in a cell; flatten to plain lines.
const plain = (md: string) => (md || '')
  .replace(/\*\*/g, '').replace(/^#+\s*/gm, '').replace(/^[-*]\s+/gm, '• ').trim();

export function exportChecklistToExcel(ck: MonthEndChecklist, narrative?: ChecklistNarrative | null): string {
  const wb = XLSX.utils.book_new();
  const used = new Set<string>();

  // ---- Summary ----
  const head: any[][] = [
    ['Month End Checklist'],
    ['Dataset', ck.dataset],
    ['Period', ck.period],
    ['Compared with', ck.prior_period || '—'],
    ['Utility', ck.utility],
    ['Verdict', ck.verdict],
    ['Exported', new Date().toLocaleString()],
    [],
  ];
  if (narrative?.overview) {
    head.push(['Cal — overview of this close']);
    plain(narrative.overview).split('\n').filter(Boolean).forEach((l) => head.push([l]));
    head.push([]);
  }
  head.push(['Group', 'Check', 'Status', 'Findings', 'Headline', 'Cal’s summary']);
  ck.items.forEach((i) => head.push([
    i.group, i.label, STATUS_LABEL[i.status] || i.status, i.finding_count, i.headline,
    plain(narrative?.items?.[i.id] || ''),
  ]));
  const ws = XLSX.utils.aoa_to_sheet(head);
  ws['!cols'] = autoWidth(head);
  XLSX.utils.book_append_sheet(wb, ws, sheetName('Summary', used));

  // ---- one sheet per check, with its FULL findings ----
  ck.items.forEach((i) => {
    const rows: any[][] = [
      [i.label], [i.group], [STATUS_LABEL[i.status] || i.status], [i.headline],
    ];
    const cal = narrative?.items?.[i.id];
    if (cal) {
      rows.push([]);
      rows.push(['Cal’s summary']);
      plain(cal).split('\n').filter(Boolean).forEach((l) => rows.push([l]));
    }
    rows.push([]);
    if (i.findings.length) {
      const cols: string[] = [];
      i.findings.forEach((r) => Object.keys(r).forEach((k) => { if (!cols.includes(k)) cols.push(k); }));
      rows.push(cols.map(headerLabel));
      i.findings.forEach((r) => rows.push(cols.map((c) => {
        const v = (r as any)[c];
        return v === null || v === undefined ? '' : v;
      })));
      if (i.finding_count > i.findings.length) {
        rows.push([`Showing ${i.findings.length} of ${i.finding_count}`]);
      }
    } else {
      rows.push(['No findings for this check.']);
    }
    const s = XLSX.utils.aoa_to_sheet(rows);
    s['!cols'] = autoWidth(rows);
    XLSX.utils.book_append_sheet(wb, s, sheetName(i.label, used));
  });

  const name = `Month-End-Checklist_${ck.dataset}_${ck.period}.xlsx`.replace(/\s+/g, '-');
  XLSX.writeFile(wb, name);
  return name;
}
